'use client';

import React, { useState } from 'react';

import { ShareProductButton } from '@/widgets/ProductDetail/_components/ShareProductButton';
import { SafeImage } from '@/widgets/SafeImage';

import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';

import { GalleryProps } from './models';

export const Gallery = ({ uuid, title, seoDescription, available, isPriceLoading, discountPercentage }: GalleryProps) => {
    const images = [
        {
            main: GenerateFilePreviewUrl({
                attachableId: uuid,
                attachment: 'PRODUCT',
                type: 'MAIN',
                isPrimary: true,
                sortOrder: 0
            }).src,
            thumbnail: GenerateFilePreviewUrl({
                attachableId: uuid,
                attachment: 'PRODUCT',
                type: 'MAIN',
                isPrimary: true,
                thumbnail: true,
                sortOrder: 0
            }).src
        }
    ];

    const [selectedIndex, setSelectedIndex] = useState(0);
    const selectedImage = images[selectedIndex] ?? images[0];

    return (
        <div className='flex flex-col gap-3'>
            <div
                className='relative aspect-square w-full overflow-hidden rounded-lg border border-solid border-gray-100 bg-white
                    [touch-action:manipulation]'>
                <SafeImage
                    fill
                    className={`object-contain p-6 transition-transform duration-300 hover:scale-105
                        ${!available && !isPriceLoading ? 'opacity-50 grayscale' : ''}`}
                    sizes='(max-width: 767px) 100vw, 50vw'
                    priority
                    fetchPriority='high'
                    src={selectedImage.main}
                    alt={title}
                    fallback='/images/noImage/noImage.png'
                />

                {!!discountPercentage && available && !isPriceLoading && (
                    <span
                        className='absolute start-4 top-4 rounded-lg bg-red-500 px-[13px] py-[5px] text-xs font-bold text-white
                            shadow-[0_4px_12px_rgba(239,68,68,0.35)]'>
                        %{discountPercentage} تخفیف
                    </span>
                )}

                <div className='absolute left-4 top-4 [&>button]:!static [&_button]:!size-11 [&_button]:!min-w-0'>
                    <ShareProductButton title={title} description={seoDescription} />
                </div>
            </div>

            <ul className='nice-scroll m-0 hidden list-none justify-center gap-2 overflow-x-auto p-0 sm:flex' aria-label='گالری تصاویر محصول'>
                {images.map((image, index) => {
                    const isSelected = index === selectedIndex;

                    return (
                        <li key={`${image.thumbnail}-${index}`} className='shrink-0'>
                            <button
                                type='button'
                                onClick={() => setSelectedIndex(index)}
                                aria-label={`تصویر ${index + 1}`}
                                aria-current={isSelected ? 'true' : undefined}
                                className={`relative size-16 overflow-hidden rounded-lg border border-solid bg-white transition-colors
                                [touch-action:manipulation] ${isSelected ? 'border-primary shadow-sm' : 'border-gray-100 hover:border-primary/40'}`}>
                                <SafeImage
                                    fill
                                    sizes='64px'
                                    className={`object-contain p-1.5 ${!available && !isPriceLoading ? 'opacity-50 grayscale' : ''}`}
                                    src={image.thumbnail}
                                    alt={`${title} - تصویر ${index + 1}`}
                                    fallback='/images/noImage/noImage.png'
                                />
                                <span
                                    className={`pointer-events-none absolute inset-0 rounded-[inherit] transition-colors
                                    ${isSelected ? 'bg-primary/15' : 'bg-black/[0.04]'}`}
                                />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
